import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReactFlow, { Background, Controls, ReactFlowProvider, MarkerType } from "reactflow";
import "reactflow/dist/style.css";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useTheme } from "@/context/ThemeContext";
import { PAGE, ICON_BTN } from "@/lib/styles";
import { ArrowLeft, ChevronLeft, ChevronRight, FileText, Lightbulb, File, Folder, CalendarCheck, X, ExternalLink, RotateCcw, ChevronsDownUp, ChevronsUpDown, Table2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = { note: "Nota", mindmap: "Mapa", sheet: "Hoja", document: "Documento", folder: "Carpeta", task: "Tarea" };
const ACTION_GROUP = { created: "created", imported: "created", converted: "created", completed: "modified", modified: "modified", deleted: "deleted" };
const ACTION_VERB = { created: "creada", imported: "importada", converted: "convertido a PDF", completed: "completada", modified: "modificada", deleted: "borrada" };

const KINDS = [
  { id: "note", label: "Notas", Icon: FileText },
  { id: "mindmap", label: "Mapas", Icon: Lightbulb },
  { id: "sheet", label: "Hojas", Icon: Table2 },
  { id: "document", label: "Documentos", Icon: File },
  { id: "folder", label: "Carpetas", Icon: Folder },
  { id: "task", label: "Tareas", Icon: CalendarCheck },
];
const ACTIONS = [
  { id: "created", label: "Creado", dot: "#10b981" },
  { id: "modified", label: "Modificado", dot: "#f59e0b" },
  { id: "deleted", label: "Borrado", dot: "#f43f5e" },
];

function palette(dark) {
  return {
    year: { background: "#6366f1", color: "#fff", border: "1px solid #818cf8" },
    month: dark ? { background: "#1e293b", color: "#e2e8f0", border: "1px solid #334155" } : { background: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0" },
    day: dark ? { background: "#0f172a", color: "#94a3b8", border: "1px solid #334155" } : { background: "#fff", color: "#475569", border: "1px solid #e2e8f0" },
    created: dark ? { background: "rgba(16,185,129,0.15)", color: "#6ee7b7", border: "1px solid rgba(16,185,129,0.35)" } : { background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0" },
    modified: dark ? { background: "rgba(245,158,11,0.15)", color: "#fcd34d", border: "1px solid rgba(245,158,11,0.35)" } : { background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" },
    deleted: dark ? { background: "rgba(244,63,94,0.15)", color: "#fda4af", border: "1px solid rgba(244,63,94,0.35)" } : { background: "#fff1f2", color: "#be123c", border: "1px solid #fecdd3" },
  };
}

const common = { borderRadius: 10, fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 12, padding: 10 };

function buildGraph(data, pal, kinds, actions, showMods) {
  const nodes = [];
  const edges = [];
  const edge = (s, t) => ({ id: `e${s}-${t}`, source: s, target: t, type: "smoothstep", style: { stroke: "#6366f1", strokeWidth: 1.5 } });

  // 1. Aplanar cronológicamente todas las actividades
  const flat = [];
  data.months.forEach((m) => m.days.forEach((d) => d.activities.forEach((a) => {
    flat.push({ ...a, monthNum: m.month, monthName: m.name, day: d.day });
  })));
  flat.sort((x, y) => x.monthNum - y.monthNum || x.day - y.day || (x.time || "").localeCompare(y.time || ""));

  // 2. Agrupar por elemento (ref_id)
  const groups = new Map();
  for (const a of flat) {
    const key = a.ref_id || `noref-${a.monthNum}-${a.day}-${a.time}-${a.title}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }

  // 3. Ubicar cada elemento bajo el día de su creación (primera actividad)
  const monthMap = new Map();
  for (const acts of groups.values()) {
    const anchor = acts[0];
    if (!kinds.has(anchor.kind)) continue;
    const anchorGrp = ACTION_GROUP[anchor.action] || "created";
    const children = acts.slice(1).filter((c) => actions.has(ACTION_GROUP[c.action] || "modified"));
    if (!actions.has(anchorGrp) && children.length === 0) continue;
    if (!monthMap.has(anchor.monthNum)) monthMap.set(anchor.monthNum, { name: anchor.monthName, days: new Map() });
    const dm = monthMap.get(anchor.monthNum).days;
    if (!dm.has(anchor.day)) dm.set(anchor.day, []);
    dm.get(anchor.day).push({ anchor, anchorGrp, children });
  }

  // 4. Layout
  let leafY = 0;
  let count = 0;
  const Y = 64;
  const DAY_GAP = 1.4;    // filas en blanco entre días
  const MONTH_GAP = 2.6;  // filas en blanco entre meses
  const X = { year: 0, month: 230, day: 520, item: 770, child: 1090 };
  const monthYs = [];
  let firstMonth = true;
  for (const mNum of [...monthMap.keys()].sort((a, b) => a - b)) {
    if (!firstMonth) leafY += MONTH_GAP;
    firstMonth = false;
    const mInfo = monthMap.get(mNum);
    const mId = `m${mNum}`;
    const dayYs = [];
    let firstDay = true;
    for (const dNum of [...mInfo.days.keys()].sort((a, b) => a - b)) {
      if (!firstDay) leafY += DAY_GAP;
      firstDay = false;
      const items = mInfo.days.get(dNum);
      const dId = `${mId}-d${dNum}`;
      const itemYs = [];
      items.forEach((it, ii) => {
        const itemId = `${dId}-i${ii}`;
        const kids = showMods ? it.children : [];
        const childYs = [];
        kids.forEach((c, ci) => {
          const cId = `${itemId}-c${ci}`;
          const y = leafY * Y; leafY += 1; childYs.push(y);
          count += 1;
          const cGrp = ACTION_GROUP[c.action] || "modified";
          const cLabel = `${c.day} ${(c.monthName || "").slice(0, 3)} \u00b7 ${c.time} \u00b7 ${ACTION_VERB[c.action] || "modificada"}`;
          nodes.push({
            id: cId, position: { x: X.child, y },
            data: { label: cLabel, ref: c.ref_id, kind: c.kind, action: cGrp, title: it.anchor.title },
            style: { ...common, ...(pal[cGrp] || pal.modified), width: 210, whiteSpace: "pre-line", cursor: "pointer", fontSize: 11 },
            sourcePosition: "right", targetPosition: "left",
          });
          edges.push(edge(itemId, cId));
        });
        let iy;
        if (childYs.length) iy = (Math.min(...childYs) + Math.max(...childYs)) / 2;
        else { iy = leafY * Y; leafY += 1; }
        itemYs.push(iy);
        count += 1;
        const head = `${it.anchor.time} \u00b7 ${KIND_LABEL[it.anchor.kind] || ""} ${ACTION_VERB[it.anchor.action] || "creada"}`;
        const collapsed = !showMods && it.children.length ? `  \u00b7 ${it.children.length} \u270e` : "";
        nodes.push({
          id: itemId, position: { x: X.item, y: iy },
          data: { label: `${head}\n${it.anchor.title}${collapsed}`, ref: it.anchor.ref_id, kind: it.anchor.kind, action: it.anchorGrp, title: it.anchor.title },
          style: { ...common, ...(pal[it.anchorGrp] || pal.created), width: 240, whiteSpace: "pre-line", cursor: "pointer" },
          sourcePosition: "right", targetPosition: "left",
        });
        edges.push(edge(dId, itemId));
      });
      if (!itemYs.length) continue;
      const dy = itemYs.reduce((a, b) => a + b, 0) / itemYs.length;
      dayYs.push(dy);
      nodes.push({ id: dId, position: { x: X.day, y: dy }, data: { label: `Día ${dNum}` }, style: { ...common, ...pal.day, width: 90, textAlign: "center" }, sourcePosition: "right", targetPosition: "left" });
      edges.push(edge(mId, dId));
    }
    if (!dayYs.length) continue;
    const my = dayYs.reduce((a, b) => a + b, 0) / dayYs.length;
    monthYs.push(my);
    nodes.push({ id: mId, position: { x: X.month, y: my }, data: { label: mInfo.name }, style: { ...common, ...pal.month, width: 120, textAlign: "center" }, sourcePosition: "right", targetPosition: "left" });
    edges.push({ ...edge("year", mId), markerEnd: { type: MarkerType.ArrowClosed, color: "#6366f1" } });
  }

  nodes.unshift({
    id: "year", position: { x: X.year, y: monthYs.length ? monthYs.reduce((a, b) => a + b, 0) / monthYs.length : 0 },
    data: { label: `${data.year}\n${count} evento(s)` },
    style: { ...common, ...pal.year, width: 140, whiteSpace: "pre-line", textAlign: "center", fontWeight: 600 },
    sourcePosition: "right", targetPosition: "left",
  });
  return { nodes, edges, count };
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-indigo-600 text-white border-indigo-600" : "bg-transparent text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-indigo-400"}`}
    >
      {children}
    </button>
  );
}

function Inner() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [year, setYear] = useState(new Date().getFullYear());
  const [raw, setRaw] = useState({ year: new Date().getFullYear(), months: [] });
  const [loading, setLoading] = useState(true);
  const [kinds, setKinds] = useState(new Set(["note", "mindmap", "sheet", "document", "folder", "task"]));
  const [actions, setActions] = useState(new Set(["created", "modified", "deleted"]));
  const [detail, setDetail] = useState(null); // { refId, kind, title, entries }
  const [showMods, setShowMods] = useState(true);

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      const { data } = await api.get("/bitacora", { params: { year: y } });
      setRaw(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(year); }, [year, load]);

  const clearYear = useCallback(async () => {
    if (!window.confirm(`¿Vaciar la bitácora de ${year}? Se borrará el historial de actividad de ese año (no borra tus documentos).`)) return;
    try {
      const { data } = await api.delete("/bitacora", { params: { year } });
      toast.success(`Bitácora ${year} vaciada (${data.deleted} registros)`);
      load(year);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, [year, load]);

  const { graph, count } = useMemo(() => {
    const g = buildGraph(raw, palette(theme === "dark"), kinds, actions, showMods);
    return { graph: { nodes: g.nodes, edges: g.edges }, count: g.count };
  }, [raw, theme, kinds, actions, showMods]);

  // Índice de historial por elemento (independiente de filtros): ref_id -> lista cronológica
  const historyIndex = useMemo(() => {
    const idx = {};
    raw.months.forEach((m) =>
      m.days.forEach((day) =>
        day.activities.forEach((a) => {
          if (!a.ref_id) return;
          (idx[a.ref_id] ||= []).push({
            monthName: m.name, day: day.day, action: a.action, kind: a.kind, title: a.title, time: a.time,
          });
        })
      )
    );
    return idx;
  }, [raw]);

  const toggle = (setFn, set, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setFn(next);
  };

  const openItem = (kind, ref, lastAction) => {
    if (!ref) return;
    if (lastAction === "deleted") { toast.info("Este elemento fue borrado"); return; }
    if (kind === "mindmap") navigate(`/mindmap/${ref}`);
    else if (kind === "sheet") navigate(`/hoja/${ref}`);
    else if (kind === "movemap") navigate(`/movimientos/${ref}`);
    else if (kind === "board") navigate(`/pizarra/${ref}`);
    else if (kind === "ledger") navigate(`/contable/${ref}`);
    else if (kind === "note") navigate(`/note/${ref}`);
    else if (kind === "task") navigate("/agenda");
    else navigate("/documentos");
  };

  const onNodeClick = (_, node) => {
    const d = node.data || {};
    if (!d.ref) return;
    setDetail({ refId: d.ref, kind: d.kind, title: d.title, entries: historyIndex[d.ref] || [] });
  };

  const bgColor = theme === "dark" ? "#1e293b" : "#e2e8f0";

  return (
    <div className={`h-screen flex flex-col ${PAGE} font-body`}>
      <Navbar />
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
        <button onClick={() => navigate("/")} className={ICON_BTN} data-testid="back-button"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="font-heading font-normal text-xl tracking-tight">Bitácora</h1>
          <p className="font-mono2 text-[11px] text-slate-400">Historial automático de tu actividad</p>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={() => setYear((y) => y - 1)} className={ICON_BTN} data-testid="year-prev"><ChevronLeft size={18} /></button>
          <span className="font-mono2 text-sm font-semibold w-14 text-center" data-testid="bitacora-year">{year}</span>
          <button onClick={() => setYear((y) => y + 1)} className={ICON_BTN} data-testid="year-next"><ChevronRight size={18} /></button>
          <button onClick={clearYear} className={`${ICON_BTN} text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10`} title={`Vaciar bitácora de ${year}`} data-testid="bitacora-clear"><Trash2 size={18} /></button>
        </div>
      </div>

      {/* Filtros visuales */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-800" data-testid="bitacora-filters">
        <span className="font-mono2 text-[11px] text-slate-400 mr-1">Categorías:</span>
        {KINDS.map(({ id, label, Icon }) => (
          <Chip key={id} active={kinds.has(id)} onClick={() => toggle(setKinds, kinds, id)} testid={`filter-kind-${id}`}>
            <Icon size={13} /> {label}
          </Chip>
        ))}
        <span className="font-mono2 text-[11px] text-slate-400 mx-1">·</span>
        <span className="font-mono2 text-[11px] text-slate-400 mr-1">Acciones:</span>
        {ACTIONS.map(({ id, label, dot }) => (
          <Chip key={id} active={actions.has(id)} onClick={() => toggle(setActions, actions, id)} testid={`filter-action-${id}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: dot }} /> {label}
          </Chip>
        ))}
        <button
          onClick={() => setShowMods((v) => !v)}
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${showMods ? "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300" : "bg-indigo-600 text-white border-indigo-600"}`}
          data-testid="toggle-mods"
          title="Mostrar u ocultar las modificaciones (sub-ramas)"
        >
          {showMods ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
          {showMods ? "Plegar modificaciones" : "Expandir modificaciones"}
        </button>
        <span className="font-mono2 text-[11px] text-slate-400 ml-auto inline-flex items-center gap-1" title="Cada elemento se ancla en su día de creación; las modificaciones cuelgan como sub-ramas">
          Las modificaciones cuelgan de cada elemento · toca cualquier nodo para ver su historial
        </span>
      </div>

      <div className="flex-1 relative">
        {!loading && count === 0 && (
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-10 text-center">
              <h3 className="font-heading font-normal text-lg">Sin actividad visible en {year}</h3>
              <p className="font-body text-sm text-slate-500 dark:text-slate-400">Crea o modifica notas, mapas y documentos, o ajusta los filtros de arriba.</p>
            </div>
          </div>
        )}
        <ReactFlow
          nodes={graph.nodes}
          edges={graph.edges}
          onNodeClick={onNodeClick}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          minZoom={0.1}
          maxZoom={2}
          nodesDraggable={false}
          nodesConnectable={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={22} size={1.5} color={bgColor} />
          <Controls showInteractive={false} />
        </ReactFlow>

        {detail && (
          <div className="absolute top-4 right-4 w-80 max-w-[90vw] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl p-4 z-20" data-testid="bitacora-detail-panel">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-heading font-medium text-sm leading-snug break-words">{detail.title || "Elemento"}</h3>
              <button onClick={() => setDetail(null)} className={ICON_BTN} data-testid="detail-close"><X size={14} /></button>
            </div>
            <p className="font-mono2 text-[11px] text-slate-400 mb-3">
              {KIND_LABEL[detail.kind === "text" ? "note" : detail.kind] || "Elemento"} · {detail.entries.length} día(s) de actividad
            </p>
            <div className="max-h-64 overflow-y-auto flex flex-col gap-2.5 pr-1">
              {detail.entries.length === 0 ? (
                <p className="font-mono2 text-[11px] text-slate-400">Sin historial.</p>
              ) : (
                detail.entries.map((e, i) => {
                  const grp = ACTION_GROUP[e.action] || "created";
                  const dot = grp === "created" ? "#10b981" : grp === "deleted" ? "#f43f5e" : "#f59e0b";
                  return (
                    <div key={`${e.monthName}-${e.day}-${i}`} className="flex items-start gap-2 border-l-2 pl-2.5" style={{ borderColor: dot }} data-testid={`detail-entry-${i}`}>
                      <div>
                        <p className="font-mono2 text-[10px] text-slate-400">{e.day} {e.monthName} · {e.time}</p>
                        <p className="font-body text-xs flex items-center gap-1">
                          {i > 0 && <RotateCcw size={11} className="text-slate-400" />}
                          <span className="capitalize">{ACTION_VERB[e.action] || e.action}</span>
                          {i === 0 && <span className="text-[10px] text-slate-400">(origen)</span>}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <button
              onClick={() => openItem(detail.kind, detail.refId, detail.entries[detail.entries.length - 1]?.action)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-sm px-4 py-2 rounded-lg transition-colors"
              data-testid="detail-open-item"
            >
              <ExternalLink size={15} /> Abrir elemento
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Bitacora() {
  return (
    <ReactFlowProvider>
      <Inner />
    </ReactFlowProvider>
  );
}
